/**
 * Workspace validation utilities
 */

import { SharedApiError } from "@ano/shared/api/shared";

export type ValidationResult =
  | { valid: true }
  | { valid: false; error: string };

// Infrastructure slugs that should NOT be treated as workspace subdomains.
// NOTE: "home" is intentionally absent — home.ano.dev returns "home" so
// App.tsx can detect personal mode. The server-side reserved list in
// packages/shared/src/mutators/index.ts DOES include "home" to prevent
// workspace creation with that slug.
const RESERVED_WORKSPACE_SLUGS = new Set([
  "api",
  "api-eu",
  "api-staging",
  "api-us",
  "app",
  "app-staging",
  "beta",
  "staging",
  "sync",
  "sync-eu",
  "sync-staging",
  "www",
  // Path-route words: a workspace with one of these slugs would have an
  // unroutable `/<slug>` URL. Mirrors the server creation gate
  // (mutators/helpers.ts) + app-route NON_WORKSPACE_SEGMENTS. "home" stays
  // absent on purpose (home.ano.dev personal-mode detection).
  "c",
  "callback",
  "call-window",
  "integrations",
]);

const WORKSPACE_SLUG_PATTERN = /^[a-z0-9-]+$/;

export function normalizeWorkspaceSlug(slug: string): string {
  return slug.trim().toLowerCase();
}

export function isReservedWorkspaceSlug(slug: string): boolean {
  return RESERVED_WORKSPACE_SLUGS.has(normalizeWorkspaceSlug(slug));
}

/**
 * Validate workspace name
 */
export function validateWorkspaceName(name: string): ValidationResult {
  const trimmed = name.trim();

  if (!trimmed) {
    return { valid: false, error: "Workspace name cannot be empty" };
  }

  if (name.length > 255) {
    return {
      valid: false,
      error: "Workspace name must be 255 characters or less",
    };
  }

  return { valid: true };
}

/**
 * Validate workspace slug (URL subdomain)
 */
export function validateWorkspaceSlug(slug: string): ValidationResult {
  const normalizedSlug = normalizeWorkspaceSlug(slug);

  if (!normalizedSlug) {
    return { valid: false, error: "Slug cannot be empty" };
  }

  if (normalizedSlug.length > 63) {
    return {
      valid: false,
      error: "Slug must be 63 characters or less",
    };
  }

  if (
    normalizedSlug.startsWith("-") ||
    normalizedSlug.endsWith("-") ||
    !WORKSPACE_SLUG_PATTERN.test(normalizedSlug)
  ) {
    return {
      valid: false,
      error: "Slug must contain only lowercase letters, numbers, and hyphens",
    };
  }

  if (isReservedWorkspaceSlug(normalizedSlug)) {
    return {
      valid: false,
      error: "That slug is reserved and cannot be used",
    };
  }

  return { valid: true };
}

/**
 * Generate a slug from a workspace name
 */
export function generateSlugFromName(name: string): string {
  return normalizeWorkspaceSlug(name)
    .replace(/[^a-z0-9\s-]/g, "") // Remove special chars
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Collapse multiple hyphens
    .replace(/^-|-$/g, ""); // Trim hyphens from ends
}

/**
 * Check if a slug is available (not taken by another workspace)
 */
export async function isSlugAvailable(
  slug: string,
  existingSlugs: string[],
): Promise<boolean> {
  const normalizedSlug = normalizeWorkspaceSlug(slug);
  return !existingSlugs.some(
    (existingSlug) => normalizeWorkspaceSlug(existingSlug) === normalizedSlug,
  );
}

/**
 * Parse workspace slug from hostname when app runs on workspace subdomains.
 */
export function getWorkspaceSlugFromHostname(hostname: string): string | null {
  const normalizedHostname = hostname.trim().toLowerCase();
  if (!normalizedHostname) return null;
  if (
    normalizedHostname === "localhost" ||
    normalizedHostname === "127.0.0.1" ||
    normalizedHostname === "::1" ||
    normalizedHostname.endsWith(".localhost")
  ) {
    return null;
  }

  const labels = normalizedHostname.split(".").filter(Boolean);
  if (labels.length < 3) return null;

  const candidateSlug = labels[0];
  const validation = validateWorkspaceSlug(candidateSlug);
  if (!validation.valid) return null;
  return candidateSlug;
}

/**
 * Strip the workspace-slug subdomain from a hostname and return the
 * base (everything after the first label). Returns `null` for
 * localhost / IP hosts.
 *
 *   acme.app-staging.ano.dev → app-staging.ano.dev
 *   acme.ano.dev             → ano.dev
 *   acme.localhost           → localhost
 *   localhost                → null
 *
 * Used to construct sibling subdomain URLs (e.g. `home.<base>`) without
 * the prior `.slice(-2)` bug that collapsed `app-staging.ano.dev` →
 * `ano.dev` and routed staging traffic to production.
 */
export function getWorkspaceBaseDomainFromHostname(
  hostname: string,
): string | null {
  const normalized = hostname.trim().toLowerCase();
  if (!normalized) return null;
  if (
    normalized === "localhost" ||
    normalized === "127.0.0.1" ||
    normalized === "::1"
  ) {
    return null;
  }
  if (normalized.endsWith(".localhost")) {
    return "localhost";
  }
  const labels = normalized.split(".").filter(Boolean);
  if (labels.length < 3) return null;
  return labels.slice(1).join(".");
}

export function getWorkspaceSlugFromLocation(
  locationLike: Pick<Location, "hostname" | "protocol">,
): string | null {
  if (locationLike.protocol === "tauri:") return null;
  return getWorkspaceSlugFromHostname(locationLike.hostname);
}

/**
 * Pull the colliding slug from a `SharedApiError`'s `details` payload.
 * The `/cp/workspaces` Worker returns `{ error: "slug_taken", details:
 * { slug: "<slug>" } }` on 409; surfacing the value lets the toast tell
 * the user EXACTLY which URL is taken so they don't have to guess
 * whether it's their name → slug derivation or a manual edit.
 */
function extractTakenSlug(error: unknown): string | null {
  if (!(error instanceof SharedApiError)) return null;
  const details = error.details;
  if (details && typeof details === "object") {
    const slug = (details as Record<string, unknown>).slug;
    if (typeof slug === "string" && slug.length > 0) return slug;
  }
  return null;
}

export function getWorkspaceSlugMutationErrorMessage(
  error: unknown,
): string | null {
  if (!(error instanceof Error) || !error.message) return null;
  const message = error.message.toLowerCase();

  if (
    message.includes("workspace slug is reserved") ||
    message.includes("slug is reserved")
  ) {
    return "That workspace URL is reserved. Edit the URL below to pick a different one.";
  }

  if (
    message.includes("workspace slug cannot be empty") ||
    message.includes("workspace slug must contain only lowercase letters") ||
    message.includes("workspace slug must be 63 characters or less")
  ) {
    return error.message;
  }

  // Slug-already-taken — match BOTH the new D1 path (`error: "slug_taken"`,
  // status 409, `details.slug` set) and the legacy Zero/PG mutator path
  // (`workspaces_slug_unique_ci` in the message). The literal "slug_taken"
  // check was the gap that surfaced as "Failed to create workspace" on
  // staging — the user couldn't tell whether it was the name or the URL
  // that needed editing.
  if (
    message === "slug_taken" ||
    message.includes("workspace slug is already taken") ||
    (message.includes("slug") &&
      (message.includes("unique") ||
        message.includes("workspaces_slug_unique") ||
        message.includes("workspaces_slug_unique_ci")))
  ) {
    const taken = extractTakenSlug(error);
    return taken
      ? `Workspace URL “${taken}” is already taken. Pick a different name or edit the URL below.`
      : "That workspace URL is already taken. Pick a different name or edit the URL below.";
  }

  return null;
}
